import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { CircularProgress } from '@material-ui/core';
import {
  Send,
  X,
  Maximize2,
  Minimize2,
  Paperclip,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'react-feather';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useFacilityModeData, Mode, ApiState } from './map/FacilityModeDataContext';
import { auth } from '../../services/firebase';
import { FileDropOverlay } from '../ui';
import useFileDrop from '../../hooks/useFileDrop';
import { ACCEPT_ATTR, IMAGE_MIME_TYPES, partitionFiles } from '../../utils/fileAccept';
import notifyRejectedFiles from '../../utils/notifyRejectedFiles';

// ─── config ──────────────────────────────────────────────────────────────────

const ENVIROCHAT_API = process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT ?? '';
const CERES_API = process.env.REACT_APP_CERES_URL;

const facilityChatStreamUrl = (mode: Mode) =>
  `${ENVIROCHAT_API}/enviro/facility_chat/${mode}/stream`;

const MODE_META: Record<Mode, { label: string; color: string; icon: string }> = {
  air: { label: 'Air', color: '#E24B4A', icon: '💨' },
  water: { label: 'Water', color: '#3FC5D5', icon: '💧' },
  land: { label: 'Land', color: '#EF9F27', icon: '⛰' },
};

// Minimize/maximize morph between the mini composer and the full panel.
const MORPH_MS = 260;
const MORPH_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const generateChatId = () => `fc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ─── types ───────────────────────────────────────────────────────────────────

type PanelPhase = 'closed' | 'opening' | 'open' | 'closing';

interface AttachFile {
  preview: string;
  name: string;
  type: string;
}

interface ConversationMessage {
  id: string;
  role: 'user' | 'system';
  content: string;
  attachFiles?: AttachFile[];
  // Live facility-data snapshot sent to the LLM at the moment this message was
  // asked. Persisted alongside the message so we can replay what context the
  // model saw. Only populated on user messages.
  metadata?: Record<string, any>;
}

interface ChatListItem {
  id: string;
  title: string;
  updated_at: string;
}

interface StoredChat {
  id: string;
  entityNumber: string;
  mode: Mode;
  title: string;
  messages: ConversationMessage[];
  updated_at: string;
}

// ─── backend (Cloud Functions REST) ──────────────────────────────────────────
// /functions/src/api/index.ts hosts these endpoints; all are uid-scoped via the
// Firebase ID token (the server derives uid from the token — never trusted from
// the client).

const authHeader = async (): Promise<Record<string, string>> => {
  const token = await auth.currentUser?.getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiListChats = async (entityNumber: string, mode: Mode): Promise<ChatListItem[]> => {
  const headers = await authHeader();
  const res = await fetch(
    `${CERES_API}/api/facility-chats?entityNumber=${encodeURIComponent(entityNumber)}&mode=${mode}`,
    { headers }
  );
  if (!res.ok) throw new Error(`List failed: HTTP ${res.status}`);
  return res.json();
};

const apiLoadChat = async (chatId: string): Promise<StoredChat | null> => {
  const headers = await authHeader();
  const res = await fetch(`${CERES_API}/api/facility-chats/${chatId}`, { headers });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Load failed: HTTP ${res.status}`);
  return res.json();
};

const apiSaveChat = async (chat: StoredChat): Promise<void> => {
  const headers = await authHeader();
  const res = await fetch(`${CERES_API}/api/facility-chats/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({
      chatId: chat.id,
      entityNumber: chat.entityNumber,
      mode: chat.mode,
      title: chat.title,
      messages: chat.messages,
    }),
  });
  if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`);
};

const apiDeleteChat = async (chatId: string): Promise<void> => {
  const headers = await authHeader();
  const res = await fetch(`${CERES_API}/api/facility-chats/${chatId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error(`Delete failed: HTTP ${res.status}`);
};

// SSE-stream the facility chat response. Mirrors the conventions used by
// enviro-chat's classify_route/stream endpoint: line-buffered `data: {…}`
// events with `type: status | token | done | error`.
const streamChat = async (
  q: string,
  mode: Mode,
  metadata: Record<string, any>,
  history: { role: 'user' | 'assistant'; content: string }[],
  files: File[],
  onStatus: (msg: string) => void,
  onToken: (chunk: string) => void,
  onDone: () => void,
  onError: (err: string) => void,
  signal: AbortSignal
) => {
  try {
    const headers = await authHeader();
    const formData = new FormData();
    formData.append('q', q);
    formData.append('mode', mode);
    formData.append('history', JSON.stringify(history));
    formData.append('metadata', JSON.stringify(metadata));
    files.forEach((f) => formData.append('files', f));

    const res = await fetch(facilityChatStreamUrl(mode), {
      method: 'POST',
      headers,
      body: formData,
      signal,
    });

    if (!res.ok) {
      if (res.status === 413) throw new Error('FILE_TOO_LARGE');
      throw new Error(`HTTP ${res.status}`);
    }
    if (!res.body) throw new Error('No response body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let carryOver = '';
    let streamDone = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (signal.aborted) break;

      const chunk = carryOver + decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');
      carryOver = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        let event: any;
        try {
          event = JSON.parse(line.slice(6));
        } catch {
          continue;
        }
        if (event.type === 'status') {
          onStatus(event.message ?? '');
        } else if (event.type === 'token') {
          onToken(event.content ?? '');
        } else if (event.type === 'done') {
          streamDone = true;
          onDone();
        } else if (event.type === 'error') {
          streamDone = true;
          onError(event.message ?? 'Server error');
        }
      }
    }

    if (!streamDone && !signal.aborted) {
      onDone();
    }
  } catch (e: any) {
    if (e?.name === 'AbortError' || signal.aborted) return;
    const msg =
      e?.message === 'FILE_TOO_LARGE'
        ? 'File is too large. Please try a smaller file.'
        : e?.message || 'Stream failed';
    onError(msg);
  }
};

// ─── animations ──────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const wave = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30%            { transform: translateY(-5px); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`;

// ─── mini composer (collapsed state) ─────────────────────────────────────────
// Sits where a chat bubble would. Focusing it expands into the full panel, so
// it mostly acts as the panel's entry point; the draft is shared state, so
// anything typed here carries over.

const MiniComposer = styled.form<{ $disabled: boolean; $dragOver: boolean; $hidden: boolean }>`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: min(420px, calc(100vw - 32px));
  z-index: 1100;
  display: flex;
  flex-direction: column;
  /* Stays mounted while the panel is up so the morph can measure it. */
  visibility: ${(p) => (p.$hidden ? 'hidden' : 'visible')};
  background: linear-gradient(180deg, rgba(12, 18, 26, 0.96) 0%, rgba(8, 13, 20, 0.98) 100%);
  border: 1px solid
    ${(p) => (p.$dragOver ? 'rgba(52, 211, 153, 0.7)' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  opacity: ${(p) => (p.$disabled ? 0.55 : 1)};
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  transition: border-color 0.15s ease;

  &:focus-within,
  &:hover {
    border-color: ${(p) => (p.$disabled ? 'rgba(255, 255, 255, 0.15)' : 'rgba(52, 211, 153, 0.5)')};
  }

  @media (max-width: 480px) {
    right: 16px;
    bottom: 16px;
  }
`;

const MiniHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 6px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const MiniTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  strong {
    color: #e2e8f0;
    font-weight: 600;
  }
`;

const MaximizeBtn = styled.button`
  position: relative;
  appearance: none;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: #94a3b8;
  cursor: pointer;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: #e2e8f0;
    background: rgba(255, 255, 255, 0.08);
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const UnreadDot = styled.span`
  position: absolute;
  top: 3px;
  right: 3px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 0 2px rgba(10, 16, 24, 1);
`;

const MiniBody = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  padding: 4px 6px 6px 4px;
`;

const MiniInput = styled.textarea`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  color: #f8fafc;
  font-size: 14px;
  font-family: inherit;
  line-height: 20px;
  /* Header (41px) + body padding (10px) + borders (2px) + 67px = 120px box at rest. */
  min-height: 120px;
  /* ~5 lines, then scroll */
  max-height: 120px;
  padding: 10px 8px 10px 10px;
  overflow-y: auto;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  &:disabled {
    cursor: not-allowed;
  }
  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
`;

const MiniActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding-bottom: 4px;
`;

const MiniStatus = styled.button`
  appearance: none;
  flex: 1;
  min-width: 0;
  height: 40px;
  border: none;
  background: transparent;
  padding: 0 8px 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 13px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
`;

// ─── modal shell ─────────────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Entry/exit motion is driven from the component (FLIP morph), not keyframes.
const Panel = styled.div`
  transform-origin: top left;
  will-change: transform, opacity;
  width: 90vw;
  height: 80vh;
  background: linear-gradient(180deg, rgba(12, 18, 26, 0.98) 0%, rgba(8, 13, 20, 0.99) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  display: flex;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.7);
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
`;

// ─── sidebar ─────────────────────────────────────────────────────────────────

const Sidebar = styled.aside<{ $open: boolean }>`
  width: ${(p) => (p.$open ? '260px' : '0')};
  overflow: hidden;
  transition: width 0.25s ease;
  background: rgba(0, 0, 0, 0.35);
  border-right: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const SidebarInner = styled.div`
  width: 260px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 14px 12px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SidebarHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SidebarTitle = styled.span`
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const NewChatBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(52, 211, 153, 0.25);
  background: rgba(52, 211, 153, 0.05);
  border-radius: 10px;
  padding: 8px 12px;
  color: #34d399;
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.15s ease;
  width: 100%;

  &:hover {
    background: rgba(52, 211, 153, 0.1);
    border-color: rgba(52, 211, 153, 0.45);
  }
`;

const SidebarList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px 8px;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const SidebarDateLabel = styled.div`
  color: #334155;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 10px 6px 4px;
`;

const SidebarChatItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  background: ${(p) => (p.$active ? 'rgba(52, 211, 153, 0.08)' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? 'rgba(52, 211, 153, 0.2)' : 'transparent')};
  transition: all 0.12s ease;

  &:hover {
    background: ${(p) => (p.$active ? 'rgba(52, 211, 153, 0.08)' : 'rgba(255, 255, 255, 0.04)')};
  }
`;

const SidebarChatTitle = styled.span`
  flex: 1;
  color: #94a3b8;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const SidebarDeleteBtn = styled.button`
  appearance: none;
  border: none;
  padding: 3px;
  background: transparent;
  color: #475569;
  cursor: pointer;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0;
  transition: all 0.12s ease;

  ${SidebarChatItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: #f87171;
    background: rgba(248, 113, 113, 0.08);
  }
`;

const SidebarEmpty = styled.div`
  color: #475569;
  font-size: 12px;
  text-align: center;
  padding: 24px 12px;
`;

// ─── main pane ───────────────────────────────────────────────────────────────

const MainPane = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
  gap: 12px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const ToggleSidebarBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  color: #64748b;
  cursor: pointer;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    color: #cbd5e1;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const HeaderFacility = styled.div`
  color: #f1f5f9;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderSub = styled.div`
  color: #64748b;
  font-size: 11px;
`;

const ModeBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => p.$color}1a;
  color: ${(p) => p.$color};
  border: 1px solid ${(p) => p.$color}55;
  white-space: nowrap;
`;

const CloseBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  color: #94a3b8;
  cursor: pointer;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #e2e8f0;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Conversation = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #64748b;
  gap: 8px;
  padding: 24px;
`;

const EmptyTitle = styled.div`
  color: #e2e8f0;
  font-size: 18px;
  font-weight: 600;
`;

const EmptyHint = styled.div`
  color: #64748b;
  font-size: 13px;
  max-width: 420px;
  line-height: 1.5;
`;

const MessageRow = styled.div<{ $role: 'user' | 'system' }>`
  display: flex;
  justify-content: ${(p) => (p.$role === 'user' ? 'flex-end' : 'flex-start')};
`;

const UserBubble = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 18px 18px 4px 18px;
  padding: 10px 16px;
  color: #f1f5f9;
  font-size: 14px;
  line-height: 1.5;
  max-width: 80%;
  animation: ${fadeSlideIn} 0.2s ease;
`;

const SystemText = styled.div`
  color: #e2e6eb;
  font-size: 14px;
  line-height: 1.6;
  max-width: 90%;

  p {
    margin: 0 0 8px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul,
  ol {
    margin: 0 0 8px 18px;
    padding: 0;
  }
  code {
    background: rgba(255, 255, 255, 0.06);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.9em;
  }
  a {
    color: #34d399;
  }
`;

const UserBubbleFiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

const MsgThumb = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
`;

const MsgFileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 3px 8px 3px 5px;
  font-size: 11px;
  color: #94a3b8;
`;

const BlinkCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 0.85em;
  background: #34d399;
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: ${blink} 0.8s step-end infinite;
`;

const ThinkingWave = styled.div`
  display: inline-flex;
  gap: 1px;
`;

const StatusText = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 13px;
`;

const WaveChar = styled.span<{ $delay: number }>`
  display: inline-block;
  color: #64748b;
  font-size: 14px;
  animation: ${wave} 1.4s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay}s;
`;

// ─── composer ────────────────────────────────────────────────────────────────

const ComposerWrap = styled.form`
  margin: 0 18px 18px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  &:focus-within {
    border-color: rgba(52, 211, 153, 0.5);
  }
`;

const QueryInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  background: transparent;
  border: none;
  outline: none;
  color: #f8fafc;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.45;
  padding: 12px 16px 8px;
  resize: none;
  min-height: 40px;
  max-height: 140px;
  display: block;

  &::placeholder {
    color: rgba(255, 255, 255, 0.32);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
`;

const FilePreviewBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 14px 0;
`;

const FilePreviewChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  padding: 4px 6px 4px 8px;
  max-width: 160px;
`;

const PreviewThumb = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
`;

const FileChipLabel = styled.span`
  color: #cbd5e1;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const RemoveChipBtn = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;

  &:hover {
    color: #e2e8f0;
  }
`;

const ComposerToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px 8px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const AttachBtn = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: transparent;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: #34d399;
    background: rgba(52, 211, 153, 0.08);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SubmitBtn = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #34d399;
  color: #000;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #3aefb8;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

// ─── delete modal ────────────────────────────────────────────────────────────

const DeleteOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.15s ease;
`;

const DeletePanel = styled.div`
  width: min(360px, calc(100vw - 32px));
  background: linear-gradient(180deg, rgba(12, 18, 26, 0.97) 0%, rgba(8, 13, 20, 0.99) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 22px;
  animation: ${fadeSlideIn} 0.18s ease;
`;

const DeleteTitle = styled.h3`
  margin: 0 0 8px;
  color: #f1f5f9;
  font-size: 15px;
  font-weight: 600;
`;

const DeleteBody = styled.p`
  margin: 0 0 18px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.55;
`;

const DeleteActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ModalCancelBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 7px 14px;
  color: #94a3b8;
  font-size: 12px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
`;

const ModalDeleteBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(248, 113, 113, 0.35);
  background: rgba(248, 113, 113, 0.08);
  border-radius: 10px;
  padding: 7px 14px;
  color: #f87171;
  font-size: 12px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
`;

// ─── helpers ─────────────────────────────────────────────────────────────────

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const groupChatsByDate = (chats: ChatListItem[]): { label: string; items: ChatListItem[] }[] => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const map = new Map<string, ChatListItem[]>();
  chats.forEach((chat) => {
    const d = new Date(chat.updated_at);
    let label: string;
    if (d.toDateString() === today.toDateString()) label = 'Today';
    else if (d.toDateString() === yesterday.toDateString()) label = 'Yesterday';
    else label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (!map.has(label)) map.set(label, []);
    map.get(label)!.push(chat);
  });
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
};

// Build the live snapshot sent with every message. Pulls only the fields shown
// in the overlay for the current mode so the LLM sees what the user sees.
const buildFacilityContext = (
  ctx: ReturnType<typeof useFacilityModeData>,
  mode: Mode
): Record<string, any> => {
  const record = ctx.activeMarkerRecord;
  if (!record) return {};

  const facility = {
    name: record.entityName,
    entityNumber: record.entityNumber,
    lat: record.location?.lat ?? null,
    lon: record.location?.lon ?? null,
    address: record.address ?? null,
    zip: ctx.zipCode,
    registryId: ctx.registryId,
  };

  const summarize = (s: ApiState) =>
    s.state === 'ok' ? s.data : s.state === 'error' ? { error: s.error } : { loading: true };

  if (mode === 'air') {
    return {
      facility,
      mode,
      modeData: {
        airnow: summarize(ctx.airnow),
        nws: summarize(ctx.nws),
        echoCAA: summarize(ctx.echo),
      },
    };
  }
  if (mode === 'water') {
    const dischargeSummary = ctx.dischargeData?.length
      ? {
          totalExceedances12mo: ctx.dischargeData.reduce(
            (s, f) => s + f.summary.totalExceedances12mo,
            0
          ),
          totalOutfalls: ctx.dischargeData.reduce((s, f) => s + f.summary.totalOutfalls, 0),
          totalParameterRows: ctx.dischargeData.reduce(
            (s, f) => s + f.summary.totalParameterRows,
            0
          ),
          mostRecentDMRPeriod:
            ctx.dischargeData.find((f) => f.summary.mostRecentDMRPeriod)?.summary
              .mostRecentDMRPeriod ?? null,
          lastExceedanceDate: ctx.dischargeData.reduce<string | null>(
            (latest, f) => f.summary.lastExceedanceDate ?? latest,
            null
          ),
        }
      : null;
    // Full per-outfall / per-parameter detail so the model can reason about
    // *which* outfall and *which* parameter is exceeding — not just the totals
    // in dischargeSummary. `sparklineValues` is dropped (chart-only, token-heavy
    // and adds no analytical value the percent/exceedance fields don't already
    // carry).
    const outfalls = (ctx.dischargeData ?? []).flatMap((f) =>
      f.outfalls.map((o) => ({
        npdesId: f.npdesId,
        id: o.id,
        typeDesc: o.typeDesc,
        complianceStatus: o.complianceStatus,
        exceedanceCount12mo: o.exceedanceCount12mo,
        lastExceedanceDate: o.lastExceedanceDate,
        lastExceedanceParam: o.lastExceedanceParam,
        lastExceedancePct: o.lastExceedancePct,
        receivingWater: o.receivingWater,
        huc12: o.huc12,
        parameters: o.parameters.map(({ sparklineValues, ...p }) => p),
      }))
    );
    return {
      facility,
      mode,
      modeData: {
        usgs: summarize(ctx.usgs),
        nwps: summarize(ctx.nwps),
        echoCWA: summarize(ctx.echo),
      },
      dischargeSummary,
      outfalls,
    };
  }
  // land
  return {
    facility,
    mode,
    modeData: {
      echoRCRA: summarize(ctx.echo),
      eq: summarize(ctx.eq),
    },
  };
};

// ─── main component ──────────────────────────────────────────────────────────

const FacilityChatBubble: React.FC = () => {
  const ctx = useFacilityModeData();
  const { activeMarkerRecord, currentMode } = ctx;
  const entityNumber = activeMarkerRecord?.entityNumber ?? null;

  // The panel is mounted for every phase but 'closed'; 'opening' and 'closing'
  // run the morph between the mini composer's box and the panel's box.
  const [phase, setPhase] = useState<PanelPhase>('closed');
  const isOpen = phase !== 'closed';
  const [hasUnread, setHasUnread] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chatList, setChatList] = useState<ChatListItem[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [activeChatTitle, setActiveChatTitle] = useState('');
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [query, setQuery] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [streamingMsgId, setStreamingMsgId] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<AttachFile[]>([]);
  const [deletePendingId, setDeletePendingId] = useState<string | null>(null);
  const [miniDragOver, setMiniDragOver] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const miniRef = useRef<HTMLFormElement>(null);
  const miniInputRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const phaseTimerRef = useRef<number | null>(null);
  // Read from stream callbacks, whose closures would otherwise see a stale phase.
  const panelVisibleRef = useRef(false);
  panelVisibleRef.current = phase === 'opening' || phase === 'open';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const streamBufferRef = useRef<{ text: string }>({ text: '' });

  const groupedChats = useMemo(() => groupChatsByDate(chatList), [chatList]);

  // Refresh sidebar list when (facility, mode) changes — and reset right pane.
  useEffect(() => {
    streamAbortRef.current?.abort();
    setActiveChatId(null);
    setActiveChatTitle('');
    setMessages([]);
    setQuery('');
    setUploadedFiles([]);
    setFilePreviews([]);
    setIsThinking(false);
    setStreamingMsgId(null);
    setStatusMessage(null);
    setChatLoading(false);
    setHasUnread(false);
    if (!entityNumber) {
      setChatList([]);
      return;
    }
    let cancelled = false;
    apiListChats(entityNumber, currentMode)
      .then((list) => {
        if (!cancelled) setChatList(list);
      })
      .catch(() => {
        if (!cancelled) {
          setChatList([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [entityNumber, currentMode]);

  // FLIP morph between the mini composer and the panel. Offsets (not
  // getBoundingClientRect) give the panel's untransformed box, so a minimize
  // that interrupts a half-finished maximize still lands on the right rect.
  useLayoutEffect(() => {
    if (phase !== 'opening' && phase !== 'closing') return;
    const panel = panelRef.current;
    const mini = miniRef.current;
    const backdrop = backdropRef.current;
    if (!panel || !mini || !backdrop) {
      setPhase(phase === 'opening' ? 'open' : 'closed');
      return;
    }
    const m = mini.getBoundingClientRect();
    const collapsed =
      `translate(${m.left - panel.offsetLeft}px, ${m.top - panel.offsetTop}px) ` +
      `scale(${m.width / panel.offsetWidth}, ${m.height / panel.offsetHeight})`;
    const duration = prefersReducedMotion() ? 0 : MORPH_MS;
    const transition = `transform ${duration}ms ${MORPH_EASE}, opacity ${duration}ms ease`;

    if (phase === 'opening') {
      panel.style.transition = 'none';
      panel.style.transform = collapsed;
      panel.style.opacity = '0.3';
      backdrop.style.transition = 'none';
      backdrop.style.opacity = '0';
      void panel.offsetWidth; // commit the start frame before transitioning
      panel.style.transition = transition;
      panel.style.transform = '';
      panel.style.opacity = '1';
      backdrop.style.transition = `opacity ${duration}ms ease`;
      backdrop.style.opacity = '1';
    } else {
      panel.style.transition = transition;
      panel.style.transform = collapsed;
      panel.style.opacity = '0.3';
      backdrop.style.transition = `opacity ${duration}ms ease`;
      backdrop.style.opacity = '0';
    }

    if (phaseTimerRef.current) window.clearTimeout(phaseTimerRef.current);
    phaseTimerRef.current = window.setTimeout(
      () => setPhase(phase === 'opening' ? 'open' : 'closed'),
      duration
    );
  }, [phase]);

  useEffect(
    () => () => {
      if (phaseTimerRef.current) window.clearTimeout(phaseTimerRef.current);
    },
    []
  );

  // Focus the panel's textarea once maximized, caret at the end of the draft.
  useEffect(() => {
    if (phase !== 'open') return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    // A carried-over multi-line draft should start at its full height.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [phase]);

  // The mini textarea grows with the draft (capped by its max-height), and the
  // draft can change from the panel too, so size it from state rather than
  // in onChange.
  useLayoutEffect(() => {
    const el = miniInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [query, phase]);

  // Esc minimizes — unless the delete confirmation is up, which it dismisses.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (deletePendingId) setDeletePendingId(null);
      else setPhase((p) => (p === 'closed' ? p : 'closing'));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, deletePendingId]);

  // Abort any in-flight stream when closing or unmounting.
  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
    };
  }, []);

  const loadChatById = async (chatId: string) => {
    if (chatId === activeChatId || chatLoading) return;
    streamAbortRef.current?.abort();
    setQuery('');
    setUploadedFiles([]);
    setFilePreviews([]);
    setIsThinking(false);
    setStreamingMsgId(null);
    setStatusMessage(null);
    setMessages([]);
    setActiveChatId(chatId);
    setChatLoading(true);
    try {
      const chat = await apiLoadChat(chatId);
      if (!chat) {
        setActiveChatId(null);
        return;
      }
      setActiveChatTitle(chat.title);
      setMessages(chat.messages);
    } catch (err) {
      setActiveChatId(null);
    } finally {
      setChatLoading(false);
    }
  };

  const startNewChat = () => {
    streamAbortRef.current?.abort();
    setActiveChatId(null);
    setActiveChatTitle('');
    setMessages([]);
    setQuery('');
    setUploadedFiles([]);
    setFilePreviews([]);
    setIsThinking(false);
    setStreamingMsgId(null);
    setStatusMessage(null);
    setChatLoading(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const confirmDelete = async () => {
    if (!deletePendingId) return;
    const idToDelete = deletePendingId;
    try {
      await apiDeleteChat(idToDelete);
    } catch (err) {
      return;
    }
    setChatList((prev) => prev.filter((c) => c.id !== idToDelete));
    if (activeChatId === idToDelete) startNewChat();
    setDeletePendingId(null);
  };

  const appendAttachmentFiles = async (selected: File[]) => {
    if (!selected.length) return;
    const updatedFiles = [...uploadedFiles, ...selected];
    setUploadedFiles(updatedFiles);
    const previews = await Promise.all(
      updatedFiles.map(async (file) => {
        if (IMAGE_MIME_TYPES.includes(file.type)) {
          const base64 = await blobToBase64(file);
          return { preview: base64, name: file.name, type: file.type };
        }
        return { preview: URL.createObjectURL(file), name: file.name, type: file.type };
      })
    );
    setFilePreviews(previews);
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []) as File[];
    e.target.value = '';
    // The picker's `accept` filter is advisory — users can switch the dialog to
    // "All files" — so validate here too and keep both paths in agreement.
    const { accepted, rejected } = partitionFiles(selected);
    notifyRejectedFiles(rejected);
    await appendAttachmentFiles(accepted);
  };

  // Drop anywhere to attach, but only while the bubble is open — a stray drop
  // on the 3D map behind it should not pull the chat into a new state.
  const { isDragging } = useFileDrop({
    onFiles: appendAttachmentFiles,
    onReject: notifyRejectedFiles,
    disabled: !isOpen || isThinking || chatLoading || !!streamingMsgId,
  });

  const removeAttachment = (idx: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== idx));
    setFilePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const persistChat = (id: string, title: string, msgs: ConversationMessage[]) => {
    if (!entityNumber) return;
    const stored: StoredChat = {
      id,
      entityNumber,
      mode: currentMode,
      title,
      messages: msgs.map((m) => ({
        ...m,
        attachFiles: m.attachFiles?.map((f) => ({ name: f.name, type: f.type, preview: '' })),
      })),
      updated_at: new Date().toISOString(),
    };
    apiSaveChat(stored);
    setChatList((prev) => {
      const entry: ChatListItem = { id, title, updated_at: stored.updated_at };
      return prev.some((c) => c.id === id)
        ? prev.map((c) => (c.id === id ? entry : c))
        : [entry, ...prev];
    });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q || isThinking || streamingMsgId || !activeMarkerRecord) return;

    setQuery('');
    const snapshotFiles = [...uploadedFiles];
    const snapshotPreviews = [...filePreviews];
    setUploadedFiles([]);
    setFilePreviews([]);

    const messagesSnapshot = [...messages];
    const userMsgId = `user-${Date.now()}`;
    const systemMsgId = `system-${Date.now()}`;

    const chatId = activeChatId ?? generateChatId();
    const chatTitle = activeChatTitle || q.slice(0, 60);
    const isNewChat = !activeChatId;
    if (isNewChat) {
      setActiveChatId(chatId);
      setActiveChatTitle(chatTitle);
    }

    const facilityContext = buildFacilityContext(ctx, currentMode);

    const userOnlyMessages: ConversationMessage[] = [
      ...messagesSnapshot,
      {
        id: userMsgId,
        role: 'user',
        content: q,
        attachFiles: snapshotPreviews,
        metadata: facilityContext,
      },
    ];
    setMessages([...userOnlyMessages, { id: systemMsgId, role: 'system', content: '' }]);
    persistChat(chatId, chatTitle, userOnlyMessages);
    setIsThinking(true);
    setStatusMessage(null);

    const history = messagesSnapshot.map((m) => ({
      role: m.role === 'system' ? ('assistant' as const) : ('user' as const),
      content: m.content,
    }));

    streamAbortRef.current?.abort();
    const abortCtrl = new AbortController();
    streamAbortRef.current = abortCtrl;
    streamBufferRef.current = { text: '' };

    const finalize = (text: string) => {
      const finalMessages: ConversationMessage[] = [
        ...userOnlyMessages,
        { id: systemMsgId, role: 'system', content: text },
      ];
      setMessages(finalMessages);
      persistChat(chatId, chatTitle, finalMessages);
      setStreamingMsgId(null);
      setIsThinking(false);
      setStatusMessage(null);
      if (!panelVisibleRef.current) setHasUnread(true);
    };

    const onStatus = (msg: string) => {
      setStatusMessage(msg || null);
    };

    const onToken = (chunk: string) => {
      streamBufferRef.current.text += chunk;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === systemMsgId ? { ...m, content: streamBufferRef.current.text } : m
        )
      );
      setIsThinking(false);
      setStatusMessage(null);
      setStreamingMsgId(systemMsgId);
    };

    const onDone = () => finalize(streamBufferRef.current.text);
    const onError = (err: string) => {
      const errText = `Sorry — something went wrong: ${err}`;
      streamBufferRef.current.text = errText;
      finalize(errText);
    };

    streamChat(
      q,
      currentMode,
      facilityContext,
      history,
      snapshotFiles,
      onStatus,
      onToken,
      onDone,
      onError,
      abortCtrl.signal
    );
  };

  const facilityName = activeMarkerRecord?.entityName ?? null;
  const disabled = !activeMarkerRecord;
  const busy = isThinking || !!streamingMsgId || chatLoading;

  const maximize = () => {
    if (disabled) return;
    setHasUnread(false);
    setPhase((p) => (p === 'open' || p === 'opening' ? p : 'opening'));
  };

  const minimize = () => setPhase((p) => (p === 'closed' || p === 'closing' ? p : 'closing'));

  // Sending from the mini composer is what expands it, so the reply streams in
  // the full panel.
  const handleMiniSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!query.trim() || disabled || busy) return;
    maximize();
    handleSubmit();
  };

  const miniDropAllowed = !disabled && !busy;

  const handleMiniDragOver = (e: React.DragEvent) => {
    if (!miniDropAllowed || !Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setMiniDragOver(true);
  };

  const handleMiniDrop = async (e: React.DragEvent) => {
    setMiniDragOver(false);
    if (!miniDropAllowed) return;
    e.preventDefault();
    const { accepted, rejected } = partitionFiles(Array.from(e.dataTransfer.files));
    notifyRejectedFiles(rejected);
    await appendAttachmentFiles(accepted);
  };

  const attachmentChips = filePreviews.length > 0 && (
    <FilePreviewBar>
      {filePreviews.map((f, i) => (
        <FilePreviewChip key={i}>
          {IMAGE_MIME_TYPES.includes(f.type) ? (
            <PreviewThumb src={f.preview} alt={f.name} />
          ) : (
            <Paperclip size={12} style={{ color: '#94a3b8', flexShrink: 0 }} />
          )}
          <FileChipLabel>{f.name}</FileChipLabel>
          <RemoveChipBtn type="button" onClick={() => removeAttachment(i)}>
            <X size={12} />
          </RemoveChipBtn>
        </FilePreviewChip>
      ))}
    </FilePreviewBar>
  );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        multiple
        accept={ACCEPT_ATTR}
        onChange={handleFileInputChange}
      />

      <MiniComposer
        ref={miniRef}
        $disabled={disabled}
        $dragOver={miniDragOver}
        $hidden={isOpen}
        aria-hidden={isOpen}
        onSubmit={handleMiniSubmit}
        onDragOver={handleMiniDragOver}
        onDragLeave={() => setMiniDragOver(false)}
        onDrop={handleMiniDrop}
        aria-label="Ask EnviroAI about this facility"
      >
        <MiniHeader>
          <MiniTitle>
            {disabled ? (
              'Ask EnviroAI'
            ) : (
              <>
                Ask EnviroAI · <strong>{facilityName ?? 'this facility'}</strong>
              </>
            )}
          </MiniTitle>
          <ModeBadge $color={MODE_META[currentMode].color}>
            <span aria-hidden="true">{MODE_META[currentMode].icon}</span>
            {MODE_META[currentMode].label}
          </ModeBadge>
          <MaximizeBtn
            type="button"
            onClick={maximize}
            disabled={disabled}
            title={hasUnread ? 'Maximize — new reply' : 'Maximize'}
            aria-label="Maximize chat"
          >
            <Maximize2 size={14} />
            {hasUnread && <UnreadDot />}
          </MaximizeBtn>
        </MiniHeader>
        {attachmentChips}
        <MiniBody>
          {busy && !disabled ? (
            <MiniStatus type="button" onClick={maximize} title="Open chat">
              <CircularProgress size={12} style={{ color: '#34d399', flexShrink: 0 }} />
              EnviroAI is replying…
            </MiniStatus>
          ) : (
            <MiniInput
              ref={miniInputRef}
              rows={1}
              value={query}
              placeholder={
                disabled
                  ? 'Select a facility to start'
                  : `Ask anything about ${facilityName ?? 'this facility'}…`
              }
              disabled={disabled}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleMiniSubmit();
                }
              }}
            />
          )}
          <MiniActions>
            <AttachBtn
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || busy}
              title="Attach a file"
            >
              <Paperclip size={15} />
            </AttachBtn>
            <SubmitBtn type="submit" disabled={!query.trim() || disabled || busy} title="Send">
              <Send size={15} />
            </SubmitBtn>
          </MiniActions>
        </MiniBody>
      </MiniComposer>

      {isOpen && (
        <Backdrop ref={backdropRef} onMouseDown={(e) => e.target === e.currentTarget && minimize()}>
          {/* Above the panel's own 1300 stacking context. */}
          <FileDropOverlay visible={isDragging} zIndex={1400} />
          <Panel ref={panelRef} onMouseDown={(e) => e.stopPropagation()}>
            <Sidebar $open={sidebarOpen}>
              <SidebarInner>
                <SidebarHeader>
                  <SidebarHeaderRow>
                    <SidebarTitle>History · {MODE_META[currentMode].label}</SidebarTitle>
                    <ToggleSidebarBtn
                      type="button"
                      onClick={() => setSidebarOpen(false)}
                      title="Hide history"
                    >
                      <ChevronLeft size={14} />
                    </ToggleSidebarBtn>
                  </SidebarHeaderRow>
                  <NewChatBtn type="button" onClick={startNewChat}>
                    <Plus size={14} />
                    New chat
                  </NewChatBtn>
                </SidebarHeader>
                <SidebarList>
                  {chatList.length === 0 ? (
                    <SidebarEmpty>No saved chats for this mode yet</SidebarEmpty>
                  ) : (
                    groupedChats.map((group) => (
                      <React.Fragment key={group.label}>
                        <SidebarDateLabel>{group.label}</SidebarDateLabel>
                        {group.items.map((chat) => (
                          <SidebarChatItem
                            key={chat.id}
                            $active={chat.id === activeChatId}
                            onClick={() => loadChatById(chat.id)}
                          >
                            <SidebarChatTitle title={chat.title}>{chat.title}</SidebarChatTitle>
                            <SidebarDeleteBtn
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletePendingId(chat.id);
                              }}
                              title="Delete chat"
                            >
                              <Trash2 size={12} />
                            </SidebarDeleteBtn>
                          </SidebarChatItem>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </SidebarList>
              </SidebarInner>
            </Sidebar>

            <MainPane>
              <Header>
                <HeaderLeft>
                  {!sidebarOpen && (
                    <ToggleSidebarBtn
                      type="button"
                      onClick={() => setSidebarOpen(true)}
                      title="Show history"
                    >
                      <ChevronRight size={14} />
                    </ToggleSidebarBtn>
                  )}
                  <HeaderTitle>
                    <HeaderFacility>{facilityName ?? 'No facility selected'}</HeaderFacility>
                    <HeaderSub>EnviroAI · live facility context</HeaderSub>
                  </HeaderTitle>
                  <ModeBadge $color={MODE_META[currentMode].color}>
                    <span aria-hidden="true">{MODE_META[currentMode].icon}</span>
                    {MODE_META[currentMode].label}
                  </ModeBadge>
                </HeaderLeft>
                <CloseBtn type="button" onClick={minimize} title="Minimize (Esc)">
                  <Minimize2 size={15} />
                </CloseBtn>
              </Header>

              <Conversation
                style={chatLoading ? { alignItems: 'center', justifyContent: 'center' } : undefined}
              >
                {chatLoading ? (
                  <CircularProgress size={28} style={{ color: '#34d399' }} />
                ) : messages.length === 0 && !isThinking ? (
                  <EmptyState>
                    <EmptyTitle>Ask EnviroAI about {facilityName ?? 'this facility'}</EmptyTitle>
                    <EmptyHint>
                      I have live {MODE_META[currentMode].label.toLowerCase()} data for this
                      facility — compliance status, monitoring readings, and the latest agency
                      records — and I'll use it to answer your question.
                    </EmptyHint>
                  </EmptyState>
                ) : (
                  messages.map((msg) => (
                    <MessageRow key={msg.id} $role={msg.role}>
                      {msg.role === 'user' ? (
                        <UserBubble>
                          {msg.attachFiles && msg.attachFiles.length > 0 && (
                            <UserBubbleFiles>
                              {msg.attachFiles.map((f, i) =>
                                IMAGE_MIME_TYPES.includes(f.type) && f.preview ? (
                                  <MsgThumb key={i} src={f.preview} alt={f.name} />
                                ) : (
                                  <MsgFileChip key={i}>
                                    <Paperclip size={11} />
                                    {f.name}
                                  </MsgFileChip>
                                )
                              )}
                            </UserBubbleFiles>
                          )}
                          {msg.content}
                        </UserBubble>
                      ) : (
                        <SystemText>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              a: ({ href, children }) => (
                                <a href={href} target="_blank" rel="noopener noreferrer">
                                  {children}
                                </a>
                              ),
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                          {streamingMsgId === msg.id && <BlinkCursor />}
                        </SystemText>
                      )}
                    </MessageRow>
                  ))
                )}
                {!chatLoading && (isThinking || !!statusMessage) && (
                  <MessageRow $role="system">
                    {statusMessage ? (
                      <StatusText>
                        <CircularProgress size={12} style={{ color: '#64748b', flexShrink: 0 }} />
                        {statusMessage}
                      </StatusText>
                    ) : (
                      <ThinkingWave>
                        {'Thinking...'.split('').map((char, i) => (
                          <WaveChar key={i} $delay={i * 0.06}>
                            {char}
                          </WaveChar>
                        ))}
                      </ThinkingWave>
                    )}
                  </MessageRow>
                )}
              </Conversation>

              <ComposerWrap onSubmit={handleSubmit}>
                {attachmentChips}
                <QueryInput
                  ref={inputRef}
                  rows={1}
                  placeholder={
                    disabled
                      ? 'Select a facility first'
                      : `Ask anything about ${facilityName ?? 'this facility'}…`
                  }
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  disabled={disabled || isThinking || !!streamingMsgId || chatLoading}
                />
                <ComposerToolbar>
                  <AttachBtn
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || isThinking || !!streamingMsgId || chatLoading}
                    title="Attach a file"
                  >
                    <Paperclip size={15} />
                  </AttachBtn>
                  <SubmitBtn
                    type="submit"
                    disabled={!query.trim() || disabled || isThinking || !!streamingMsgId}
                  >
                    {isThinking ? (
                      <CircularProgress size={14} style={{ color: '#000' }} />
                    ) : (
                      <Send size={15} />
                    )}
                  </SubmitBtn>
                </ComposerToolbar>
              </ComposerWrap>
            </MainPane>
          </Panel>
        </Backdrop>
      )}

      {deletePendingId && (
        <DeleteOverlay
          onMouseDown={(e) => e.target === e.currentTarget && setDeletePendingId(null)}
        >
          <DeletePanel onMouseDown={(e) => e.stopPropagation()}>
            <DeleteTitle>Delete chat?</DeleteTitle>
            <DeleteBody>
              This conversation will be permanently removed. This action can't be undone.
            </DeleteBody>
            <DeleteActions>
              <ModalCancelBtn type="button" onClick={() => setDeletePendingId(null)}>
                Cancel
              </ModalCancelBtn>
              <ModalDeleteBtn type="button" onClick={confirmDelete}>
                Delete
              </ModalDeleteBtn>
            </DeleteActions>
          </DeletePanel>
        </DeleteOverlay>
      )}
    </>
  );
};

export default FacilityChatBubble;
